import { GalleryFeedbackOptions, GalleryStatus, TagColors } from '@nusign/picflow-backend-types';
import Stripe from 'stripe';
import { getBytesFromGigabytes, getBytesFromMegabytes } from './billing-consts';

export const baseZoom = 1;

export const storyblokImageSizes = [2400, 1920, 1280, 640, 0];
export const storyblokLowQualityMap = {
  2400: 85,
  1920: 85,
  1280: 85,
  640: 75,
};

export const assetFilenameSpace = 30;

export const multiPartUploadMinSize = 1024 * 1024 * 10;

export const defaultReceiptEmail = 'receipts@picflow.com';

export const multiPartUploadConcurrency = 1;
export const estimatedTimeToCreateAsset = 250;

export const defaultGalleryIncludes =
  'include[]=is_favourite&include[]=unseen_activities&include[]=tenant_product_name&include[]=parent_gallery_id';

export const GALLERY_DOWNLOAD_SIZE = {
  original: 0,
  large: 2400,
  medium: 1920,
  small: 720,
};

export const downloadSizes: { name: string; size?: number }[] = [
  {
    name: 'reserved',
  },
  {
    name: 'reserved',
  },
  {
    name: 'reserved',
  },
  {
    name: 'reserved',
  },
  {
    name: 'small',
    size: GALLERY_DOWNLOAD_SIZE.small,
  },
  {
    name: 'medium',
    size: GALLERY_DOWNLOAD_SIZE.medium,
  },
  {
    name: 'large',
    size: GALLERY_DOWNLOAD_SIZE.large,
  },
  {
    name: 'original',
  },
];

export const languages: string[] = ['en', 'de', 'fr'];

export const defaultLanguage = 'en';

export const doubleClickTime = 350;

export const biggestGroupTimeDiff = 1000 * 60 * 10;

export const maxAnimatedGifSize = 1024 * 1024 * 3;

export const maxConcurrentUploads = 5;
export const maxConcurrentUploadFileSize = Number(getBytesFromMegabytes(100n));

export const activityPerPage = 50;

export const dragScrollThreshold = 50;

export const assetLoadSettings = {
  galleryScreensToPreloadBefore: 0.5,
  galleryScreensToPreloadAfter: 1,
  galleryScreensToPreGenerate: 5,
  assetPagePreGenerateBefore: 1,
  assetPagePreloadBefore: 1,
  assetPagePreloadAfter: 3,
  assetPagePreGenerateAfter: 10,
  preGenerateHighResFromList: true,
};

export const bigIntDividePresision = 1000000n;

export const assetSizes = [480, 720, 1280, 1920, 2400, 3840, 99999999999999];
export const preGenerateOnUploadSizes = assetSizes.slice(0, 2);

export const assetSizesBigToSmall = [...assetSizes].reverse();

export const defaultDarkBackgroundThreshold = 186;

export const trialValueMultiplier = 0.5;

export const thirdPartyScriptDelay = 1000 * 2;

export const tooltipDelay = 300;

export const assetSideMenuSize = 400;

export const maxWatermarkApplyTime = 1000 * 60 * 5;

export const maxUploadRetryCount = 1;
export const maxPostAssetRetryCount = 1;
export const maxUploadFullRetryCount = 1;

export const maxZipRequestSize = 1024 * 1024 * 1024 * 5;

export const maxResizeBatchRetryCount = 2;
export const assetsPerResizeBatch = 100;

export const specialCaseFormats = ['png', 'webp', 'gif', 'tif', 'tiff'];

export const resizedImageDownloadFormatMap = {
  webp: 'webp',
  gif: 'gif',
  tif: 'jpg',
  tiff: 'jpg',
  png: 'png',
};

export const browserRenderableExtensions = ['jpg', 'jpeg', 'gif', 'png', 'webp', 'jfif'];
export const allowedTypes = ['jpg', 'jpeg', 'jfif', 'gif', 'png', 'tiff', 'tif', 'webp', 'avif', 'heic', 'heif'];
export const allowedVideoTypes = ['mp4', 'mov', 'avi', 'webm', 'm4v', 'mkv'];
export const allowedRawTypes = [
  'nef',
  'raf',
  'dng',
  'cr2',
  'cr3',
  'nrw',
  'arw',
  'pef',
  'ptx',
  '3fr',
  'fff',
  'orf',
  'iiq',
  'rw2',
  'raw',
  'rwl',
  'dcr',
];

export const accessLevelOrder = ['no_access', 'view_only', 'review', 'edit'];

export const maxTimeBetweenReloads = 1000 * 60 * 60 * 12;

export const maxFileSize = Number(getBytesFromGigabytes(100n));
export const maxGifSize = Number(getBytesFromMegabytes(50n)); // 50 MB because we don't resize gifs

export const trueUpDurationInDays = 14;

export const msInADay = 1000 * 60 * 60 * 24;

export const debugMaxEntries = 1000;

export const tagColors: TagColors[] = [null, 'red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink'];

export const fontSizeMap = {
  small: 1,
  medium: 2,
  large: 3,
  huge: 4,
};

export const GbForStoragePrice = (price: Stripe.Price) => {
  if (price.lookup_key.includes('_1tb_')) return 1000;
  return 250;
};

export const allFeedbackOptions: GalleryFeedbackOptions[] = [
  'favourite',
  'comment',
  'status',
  'color_label',
  'annotation',
];

export const feedbackOptionToScopeMapping = {
  favourite: 'asset:favourites',
  color_label: 'asset:color-labels',
  status: 'asset:status',
  comment: 'asset:comments',
  annotation: 'asset:comments',
};

// This is not great.. need to move this to a backend endpoint

export const extraParamsForCreateGalleryFromPreset = {
  title: '',
  cover: {
    overlay_color: 'rgba(31,35,46,0.2)',
    position: '50% 50%',
    size: 'small',
    uuid: '',
    content_position: <[number, number]>[0.5, 0.5],
  },
};

export const visitorsCacheMaxAge = 1000 * 60 * 5;
export const membersCacheMaxAge = 1000 * 60 * 5;
export const userGroupsMaxAge = 1000 * 60 * 5;
export const galleryMembersMaxAge = 1000 * 60 * 5;

export const minTimeBetweenTrialsInDays = 180;

export const allGalleryStatuses: GalleryStatus[] = ['approved', 'review', 'no_status', 'progress']; // 'revisions'

export const warningColorPercentage = 15;
export const errorColorPercentage = 5;

export const getRolePermissions = (role: string) => {
  if (!role) return undefined;
  if (role === 'owner') {
    return [
      'tenant-colors:list',
      'tenant-colors:retrieve',
      'multipart:abort',
      'multipart:complete',
      'bulks:retrieve',
      'auth:identification',
      'auth:user',
      'auth:change-password',
      'auth:change-email',
      'tenants:list',
      'profile:retrieve',
      'profile:update',
      'accounts:merge',
      'identities:list',
      'identities:create',
      'identities:delete',
      'identities:relink',
      'watchers:list-own',
      'watchers:delete',
      'galleries:list-shared',
      'assets:upload-user',
      'visitors:create',
      'folders:list-shared',
      'app:bulk',
      'visitors:list',
      'tenants:retrieve',
      'tenants:update',
      'tenants:overview',
      'members:list',
      'members:retrieve',
      'invitations:list',
      'invitations:retrieve',
      'subscriptions:retrieve',
      'customers:retrieve',
      'gallery-presets:list',
      'gallery-presets:create',
      'gallery-presets:retrieve',
      'gallery-presets:update',
      'gallery-presets:delete',
      'gallery-invitations:list',
      'gallery-invitations:create',
      'gallery-invitations:retrieve',
      'gallery-invitations:update',
      'gallery-invitations:delete',
      'domains:list',
      'domains:retrieve',
      'watermarks:list',
      'watermarks:create',
      'watermarks:retrieve',
      'watermarks:update',
      'watermarks:delete',
      'galleries:list',
      'galleries:create',
      'galleries:retrieve',
      'galleries:update',
      'galleries:delete',
      'galleries:overview',
      'galleries:retrieve-by-link',
      'galleries:bypass-password',
      'gallery-members:list',
      'gallery-members:create',
      'gallery-members:retrieve',
      'gallery-members:update',
      'gallery-members:delete',
      'gallery-members:delete-any',
      'gallery-viewers:list',
      'gallery-viewers:update',
      'assets:list',
      'assets:create',
      'assets:retrieve',
      'assets:update',
      'assets:delete',
      'assets:like',
      'galleries:update-protected-fields',
      'assets:attach-tag',
      'assets:detach-tag',
      'asset-versions:list',
      'asset-versions:create',
      'asset-versions:retrieve',
      'asset-versions:update',
      'asset-versions:delete',
      'sections:list',
      'sections:create',
      'sections:retrieve',
      'sections:update',
      'sections:delete',
      'tags:list',
      'tags:create',
      'tags:retrieve',
      'tags:update',
      'tags:delete',
      'folders:list',
      'folders:retrieve',
      'folders:create',
      'folders:update',
      'folders:delete',
      'comments:list',
      'comments:create',
      'comments:retrieve',
      'comments:update',
      'comments:delete',
      'comments:like',
      'watchers:list',
      'watchers:create',
      'activities:list',
      'activities:update',
      'gallery-views:create',
      'archives:create',
      'single-asset-downloads:create',
      'user-gallery-favourites:create',
      'user-gallery-favourites:delete',
      'user-folder-favourites:create',
      'user-folder-favourites:delete',
      'groups:list',
      'groups:retrieve',
      'group-users:list',
      'group-galleries:list',
      'folder-invitations:list',
      'folder-invitations:create',
      'folder-invitations:retrieve',
      'folder-invitations:update',
      'folder-invitations:delete',
      'folder-members:list',
      'folder-members:create',
      'folder-members:retrieve',
      'folder-members:update',
      'folder-members:delete',
      'folder-members:delete-any',
      'group-folders:list',
      'ai-image-generations:create',
      'credit:list',
      'credit:overview',
      'tenant-colors:create',
      'tenant-colors:update',
      'tenant-colors:delete',
      'comments:list-private',
      'visitors:delete',
      'asset-versions:access-original',
      'assets:upload-tenant',
      'invitations:create',
      'invitations:update',
      'invitations:delete',
      'domains:create',
      'domains:delete',
      'referrals:list',
      'referrals:delete',
      'subscriptions:create',
      'subscriptions:update',
      'subscriptions:cancel',
      'cards:list',
      'cards:create',
      'cards:delete',
      'invoices:list',
      'customers:update',
      'tenants:update-all-fields',
      'galleries:retrieve-any',
      'galleries:transfer-ownership',
      'folders:retrieve-any',
      'groups:create',
      'groups:update',
      'groups:delete',
      'group-users:add',
      'group-users:remove',
      'group-galleries:detach',
      'group-galleries:update',
      'comments:update-any',
      'comments:delete-any',
      'group-folders:detach',
      'group-folders:update',
    ];
  } else if (role === 'admin') {
    return [
      'tenant-colors:list',
      'tenant-colors:retrieve',
      'multipart:abort',
      'multipart:complete',
      'bulks:retrieve',
      'auth:identification',
      'auth:user',
      'auth:change-password',
      'auth:change-email',
      'tenants:list',
      'profile:retrieve',
      'profile:update',
      'accounts:merge',
      'identities:list',
      'identities:create',
      'identities:delete',
      'identities:relink',
      'watchers:list-own',
      'watchers:delete',
      'galleries:list-shared',
      'assets:upload-user',
      'visitors:create',
      'folders:list-shared',
      'app:bulk',
      'visitors:list',
      'tenants:retrieve',
      'tenants:update',
      'tenants:overview',
      'members:list',
      'members:retrieve',
      'invitations:list',
      'invitations:retrieve',
      'subscriptions:retrieve',
      'customers:retrieve',
      'gallery-presets:list',
      'gallery-presets:create',
      'gallery-presets:retrieve',
      'gallery-presets:update',
      'gallery-presets:delete',
      'gallery-invitations:list',
      'gallery-invitations:create',
      'gallery-invitations:retrieve',
      'gallery-invitations:update',
      'gallery-invitations:delete',
      'domains:list',
      'domains:retrieve',
      'watermarks:list',
      'watermarks:create',
      'watermarks:retrieve',
      'watermarks:update',
      'watermarks:delete',
      'galleries:list',
      'galleries:create',
      'galleries:retrieve',
      'galleries:update',
      'galleries:delete',
      'galleries:overview',
      'galleries:retrieve-by-link',
      'galleries:bypass-password',
      'gallery-members:list',
      'gallery-members:create',
      'gallery-members:retrieve',
      'gallery-members:update',
      'gallery-members:delete',
      'gallery-members:delete-any',
      'gallery-viewers:list',
      'gallery-viewers:update',
      'assets:list',
      'assets:create',
      'assets:retrieve',
      'assets:update',
      'assets:delete',
      'assets:like',
      'galleries:update-protected-fields',
      'assets:attach-tag',
      'assets:detach-tag',
      'asset-versions:list',
      'asset-versions:create',
      'asset-versions:retrieve',
      'asset-versions:update',
      'asset-versions:delete',
      'sections:list',
      'sections:create',
      'sections:retrieve',
      'sections:update',
      'sections:delete',
      'tags:list',
      'tags:create',
      'tags:retrieve',
      'tags:update',
      'tags:delete',
      'folders:list',
      'folders:retrieve',
      'folders:create',
      'folders:update',
      'folders:delete',
      'comments:list',
      'comments:create',
      'comments:retrieve',
      'comments:update',
      'comments:delete',
      'comments:like',
      'watchers:list',
      'watchers:create',
      'activities:list',
      'activities:update',
      'gallery-views:create',
      'archives:create',
      'single-asset-downloads:create',
      'user-gallery-favourites:create',
      'user-gallery-favourites:delete',
      'user-folder-favourites:create',
      'user-folder-favourites:delete',
      'groups:list',
      'groups:retrieve',
      'group-users:list',
      'group-galleries:list',
      'folder-invitations:list',
      'folder-invitations:create',
      'folder-invitations:retrieve',
      'folder-invitations:update',
      'folder-invitations:delete',
      'folder-members:list',
      'folder-members:create',
      'folder-members:retrieve',
      'folder-members:update',
      'folder-members:delete',
      'folder-members:delete-any',
      'group-folders:list',
      'ai-image-generations:create',
      'credit:list',
      'credit:overview',
      'tenant-colors:create',
      'tenant-colors:update',
      'tenant-colors:delete',
      'comments:list-private',
      'visitors:delete',
      'asset-versions:access-original',
      'assets:upload-tenant',
      'invitations:create',
      'invitations:update',
      'invitations:delete',
      'domains:create',
      'domains:delete',
      'referrals:list',
      'referrals:delete',
      'subscriptions:create',
      'subscriptions:update',
      'subscriptions:cancel',
      'cards:list',
      'cards:create',
      'cards:delete',
      'invoices:list',
      'customers:update',
      'tenants:update-all-fields',
      'galleries:retrieve-any',
      'galleries:transfer-ownership',
      'folders:retrieve-any',
      'groups:create',
      'groups:update',
      'groups:delete',
      'group-users:add',
      'group-users:remove',
      'group-galleries:detach',
      'group-galleries:update',
      'comments:update-any',
      'comments:delete-any',
      'group-folders:detach',
      'group-folders:update',
    ];
  } else {
    return [
      'tenant-colors:list',
      'tenant-colors:retrieve',
      'multipart:abort',
      'multipart:complete',
      'bulks:retrieve',
      'auth:identification',
      'auth:user',
      'auth:change-password',
      'auth:change-email',
      'tenants:list',
      'profile:retrieve',
      'profile:update',
      'accounts:merge',
      'identities:list',
      'identities:create',
      'identities:delete',
      'identities:relink',
      'watchers:list-own',
      'watchers:delete',
      'galleries:list-shared',
      'assets:upload-user',
      'visitors:create',
      'folders:list-shared',
      'app:bulk',
      'visitors:list',
      'tenants:retrieve',
      'tenants:update',
      'tenants:overview',
      'members:list',
      'members:retrieve',
      'invitations:list',
      'invitations:retrieve',
      'subscriptions:retrieve',
      'customers:retrieve',
      'gallery-presets:list',
      'gallery-presets:create',
      'gallery-presets:retrieve',
      'gallery-presets:update',
      'gallery-presets:delete',
      'gallery-invitations:list',
      'gallery-invitations:create',
      'gallery-invitations:retrieve',
      'gallery-invitations:update',
      'gallery-invitations:delete',
      'domains:list',
      'domains:retrieve',
      'watermarks:list',
      'watermarks:create',
      'watermarks:retrieve',
      'watermarks:update',
      'watermarks:delete',
      'galleries:list',
      'galleries:create',
      'galleries:retrieve',
      'galleries:update',
      'galleries:delete',
      'galleries:overview',
      'galleries:retrieve-by-link',
      'galleries:bypass-password',
      'gallery-members:list',
      'gallery-members:create',
      'gallery-members:retrieve',
      'gallery-members:update',
      'gallery-members:delete',
      'gallery-members:delete-any',
      'gallery-viewers:list',
      'gallery-viewers:update',
      'assets:list',
      'assets:create',
      'assets:retrieve',
      'assets:update',
      'assets:delete',
      'assets:like',
      'galleries:update-protected-fields',
      'assets:attach-tag',
      'assets:detach-tag',
      'asset-versions:list',
      'asset-versions:create',
      'asset-versions:retrieve',
      'asset-versions:update',
      'asset-versions:delete',
      'sections:list',
      'sections:create',
      'sections:retrieve',
      'sections:update',
      'sections:delete',
      'tags:list',
      'tags:create',
      'tags:retrieve',
      'tags:update',
      'tags:delete',
      'folders:list',
      'folders:retrieve',
      'folders:create',
      'folders:update',
      'folders:delete',
      'comments:list',
      'comments:create',
      'comments:retrieve',
      'comments:update',
      'comments:delete',
      'comments:like',
      'watchers:list',
      'watchers:create',
      'activities:list',
      'activities:update',
      'gallery-views:create',
      'archives:create',
      'single-asset-downloads:create',
      'user-gallery-favourites:create',
      'user-gallery-favourites:delete',
      'user-folder-favourites:create',
      'user-folder-favourites:delete',
      'groups:list',
      'groups:retrieve',
      'group-users:list',
      'group-galleries:list',
      'folder-invitations:list',
      'folder-invitations:create',
      'folder-invitations:retrieve',
      'folder-invitations:update',
      'folder-invitations:delete',
      'folder-members:list',
      'folder-members:create',
      'folder-members:retrieve',
      'folder-members:update',
      'folder-members:delete',
      'folder-members:delete-any',
      'group-folders:list',
      'ai-image-generations:create',
      'credit:list',
      'credit:overview',
    ];
  }
};
